import { FormVersionRequestParams, FormVersionRequestQuery, FormVersionSearchResponse } from '@lio/api-types';
import { formVersionQueryKeys } from '@services/formVersion/keys';
import { useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';

/** Extends the api-types query to allow false values — available after api-types republish */
type ExtendedFormVersionRequestQuery = Omit<FormVersionRequestQuery, 'isAvailable' | 'isVisible'> & {
  isAvailable?: boolean;
  isVisible?: boolean;
};

const getQuery = (params?: ExtendedFormVersionRequestQuery) => {
  const query = new URLSearchParams();

  if (params?.isAvailable !== undefined) query.append('isAvailable', String(params.isAvailable));
  if (params?.isVisible !== undefined) query.append('isVisible', String(params.isVisible));

  if (params?.role) {
    buildQueryParamForArray(query, 'role', params.role);
  }

  return query.toString();
};

export const useSearchFormVersions = (
  params: (FormVersionRequestParams & ExtendedFormVersionRequestQuery) | { quoteId: undefined },
  options?: { enabled?: boolean },
) => {
  const client = useClient();
  const { quoteId, ...queryParams } = params as FormVersionRequestParams & ExtendedFormVersionRequestQuery;
  const qs = getQuery(queryParams);

  const enabled = options?.enabled ?? true;

  const result = useQuery<FormVersionSearchResponse, Error>({
    queryKey: formVersionQueryKeys.searchWithParams(
      params as FormVersionRequestParams & ExtendedFormVersionRequestQuery as FormVersionRequestParams &
        FormVersionRequestQuery,
    ),
    queryFn: () => client.get(`quote/${quoteId}/form-version${qs ? `?${qs}` : ''}`).json<FormVersionSearchResponse>(),
    enabled: !!quoteId && enabled,
    staleTime: 20000,
  });

  return { ...result, formVersions: result.data };
};
